import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import styles from "./css/CreatePostMap.css";

/* global TMap */

const CreatePostMap = forwardRef(({ onPositionSelected }, ref) => {
  const mapContainerRef = useRef(null);
  const mapInstance = useRef(null);
  const markerLayer = useRef(null);
  const [selectedPosition, setSelectedPosition] = useState(null);

  // Function to center the map on specific coordinates
  const centerMap = (latitude, longitude) => {
    if (!mapInstance.current) return;
    
    try {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        const location = new TMap.LatLng(lat, lng);
        mapInstance.current.setCenter(location);
        mapInstance.current.setZoom(15); // Set an appropriate zoom level
        console.log(`CreatePostMap - Centered map on: Lat: ${lat}, Lng: ${lng}`);
        
        // Add a marker at the centered location
        if (markerLayer.current) {
          // Clear existing markers
          markerLayer.current.setGeometries([]);
          
          // Add a new marker
          markerLayer.current.setGeometries([{
            id: 'selected-position',
            styleId: 'marker',
            position: location,
            properties: {
              title: "Selected Position"
            }
          }]);
          
          // Update selected position and notify parent
          const newPosition = { longitude: lng, latitude: lat };
          setSelectedPosition(newPosition);
          if (typeof onPositionSelected === 'function') {
            onPositionSelected(newPosition);
          }
        }
      } else {
        console.error("CreatePostMap - Invalid coordinates:", latitude, longitude);
      }
    } catch (error) {
      console.error("CreatePostMap - Error centering map:", error);
    }
  };

  // Expose the centerMap method to parent components
  useImperativeHandle(ref, () => ({
    centerMap
  }), []);

  useEffect(() => {
    console.log("CreatePostMap - Mounted, onPositionSelected:", onPositionSelected);
    
    // DEBUG: Check if map container is visible and has proper dimensions
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      console.log("CreatePostMap - Map container dimensions:", rect.width, rect.height);
      if (rect.width === 0 || rect.height === 0) {
        console.warn("CreatePostMap - Map container has zero width or height");
      }
    } else {
      console.warn("CreatePostMap - Map container ref is null");
    }
    
    // Check if TMap is already loaded
    const initializeMap = () => {
      if (window.TMap) {
        // TMap is already loaded, initialize map directly
        console.log("CreatePostMap - TMap already loaded, initializing map directly");
        initMap();
      } else {
        // Load TMap script dynamically only if it's not already loaded
        const existingScript = document.getElementById('TmapsScript');
        if (!existingScript) {
          console.log("CreatePostMap - Loading TMap script");
          
          // Define the callback function globally
          window.initTCreatePostMap = function() {
            console.log("CreatePostMap - TMap API loaded successfully for container");
            initMap();
          };
          
          const script = document.createElement("script");
          script.id = 'TmapsScript';
          script.charset = "utf-8";
          script.src = `https://map.qq.com/api/gljs?v=1.exp&key=GDTBZ-QD4WC-G5722-A3V7A-6BPM3-35B7P&callback=initTCreatePostMap`;
          script.onerror = (error) => {
            console.error("CreatePostMap - TMap script loading error:", error);
          };
          
          document.head.appendChild(script);
          console.log("CreatePostMap - TMap script added to document head");
        } else {
          // Script exists but TMap isn't loaded yet, set up a new callback
          console.log("CreatePostMap - TMap script exists but not loaded, setting up callback");
          window.initTCreatePostMap = function() {
            console.log("CreatePostMap - TMap API loaded successfully for container");
            initMap();
          };
        }
      }
    };

    const initMap = () => {
      try {
        console.log("CreatePostMap - Initializing TMap Container");
        console.log("CreatePostMap - TMap API available:", !!window.TMap);
        
        if (!window.TMap) {
          console.error("CreatePostMap - TMap API not available");
          return;
        }
        
        // DEBUG: Check if map container exists
        if (!mapContainerRef.current) {
          console.error("CreatePostMap - Map container not found");
          return;
        }
        
        // Default center coordinates (Guangzhou)
        const defaultCenter = new TMap.LatLng(23.129110, 113.264385);
        
        // Create map instance with proper settings
        console.log("CreatePostMap - Creating map instance");
        mapInstance.current = new TMap.Map(mapContainerRef.current, {
          center: defaultCenter,
          zoom: 12,  // Set zoom level as in the example
        });
        
        // Try to get user's location
        if ("geolocation" in navigator) {
          console.log("CreatePostMap - Requesting user location");
          navigator.geolocation.getCurrentPosition(
            // Success callback - user allowed location access
            (position) => {
              const { latitude, longitude } = position.coords;
              console.log(`CreatePostMap - User's current position: Latitude: ${latitude}, Longitude: ${longitude}`);
              
              try {
                // Center the map on the user's location
                mapInstance.current.setCenter(new TMap.LatLng(parseFloat(latitude), parseFloat(longitude)));
                console.log("CreatePostMap - Map centered on user location");
              } catch (error) {
                console.error("CreatePostMap - Error setting map center to user location:", error);
                // Fallback to Guangzhou if there's an error
                mapInstance.current.setCenter(defaultCenter);
                console.log("CreatePostMap - Fallback: Map centered on Guangzhou");
              }
            },
            // Error callback - user denied location or other error
            (error) => {
              console.error("CreatePostMap - Error getting user's location:", error.message);
              // Fallback to Guangzhou
              mapInstance.current.setCenter(defaultCenter);
              console.log("CreatePostMap - Fallback: Map centered on Guangzhou");
            }
          );
        } else {
          // Geolocation not supported, center on Guangzhou
          console.log("CreatePostMap - Geolocation is not supported by this browser");
          mapInstance.current.setCenter(defaultCenter);
          console.log("CreatePostMap - Fallback: Map centered on Guangzhou");
        }

        // Initialize marker layer with empty geometries
        markerLayer.current = new TMap.MultiMarker({
          id: 'marker-layer',
          map: mapInstance.current,
          styles: {
            "marker": new TMap.MarkerStyle({
              "width": 25,
              "height": 35,
              "anchor": { x: 12, y: 35 },
              "src": '/markers/cat-marker.png'
            })
          },
          geometries: []
        });

        // Add click event listener to the map
        console.log("CreatePostMap - Adding click event listener to map");
        mapInstance.current.on('click', (evt) => {
          try {
            const lat = evt.latLng.lat;
            const lng = evt.latLng.lng;
            console.log("CreatePostMap - Map clicked at:", lat, lng);

            // Check if the coordinates are valid numbers
            if (isNaN(lng) || isNaN(lat)) {
              console.error("CreatePostMap - Invalid coordinates:", lng, lat);
              return;
            }

            const newPosition = { longitude: lng, latitude: lat };
            console.log("CreatePostMap - Setting selected position:", newPosition);
            setSelectedPosition(newPosition);
            
            // Clear existing markers by setting empty geometries
            console.log("CreatePostMap - Clearing existing markers");
            markerLayer.current.setGeometries([]);
            
            // Add a new marker at the clicked position
            console.log("CreatePostMap - Creating new marker with custom icon at:", lat, lng);
            markerLayer.current.setGeometries([{
              id: 'selected-position',
              styleId: 'marker',
              position: new TMap.LatLng(lat, lng),
              properties: {
                title: "Selected Position"
              }
            }]);
            console.log("CreatePostMap - Marker created successfully");
            
            // Ensure zoom level is maintained after adding marker
            mapInstance.current.setZoom(13);
            
            // Pass the selected position to the parent component
            console.log("CreatePostMap - Calling onPositionSelected with:", newPosition);
            console.log("CreatePostMap - onPositionSelected type:", typeof onPositionSelected);
            if (typeof onPositionSelected === 'function') {
              onPositionSelected(newPosition);
              console.log("CreatePostMap - onPositionSelected called successfully");
            } else {
              console.error("CreatePostMap - onPositionSelected is not a function:", onPositionSelected);
            }
          } catch (error) {
            console.error("CreatePostMap - Error handling map click:", error);
          }
        });
        console.log("CreatePostMap - Click event listener added successfully");

      } catch (error) {
        console.error("CreatePostMap - Map initialization error:", error);
      }
    };

    console.log("CreatePostMap - Starting map initialization");
    initializeMap();

    return () => {
      // Clean up
      console.log("CreatePostMap - Cleaning up");
      if (window.initTCreatePostMap) {
        console.log("CreatePostMap - Removing initTCreatePostMap from window");
        delete window.initTCreatePostMap;
      }
      
      // Properly clean up the map instance
      if (mapInstance.current) {
        console.log("CreatePostMap - Removing map instance reference");
        // Don't try to remove all listeners at once as it's not supported
        // Just set to null to allow garbage collection
        mapInstance.current = null;
      }
      
      // Clear the map container
      if (mapContainerRef.current) {
        while (mapContainerRef.current.firstChild) {
          mapContainerRef.current.removeChild(mapContainerRef.current.firstChild);
        }
      }
    };
  }, [onPositionSelected]);

  return (
    <div>
      <div
        ref={mapContainerRef}
        className={styles.container}
        style={{ height: "400px" }}
      ></div>
      <div className={styles.info}>
        {selectedPosition && (
          <p>
            事件地点: N: {selectedPosition.latitude.toFixed(6)}, 
            E: {selectedPosition.longitude.toFixed(6)}
          </p>
        )}
      </div>
    </div>
  );
});

export default CreatePostMap;
